/**
 * Invitation acceptance.
 *
 * The token is in the URL path, so it is single-use and bound to one mailbox
 * server-side. The interesting failure is the wrong-account case: somebody signed in
 * as their personal address clicking an invitation sent to their work address. The
 * server refuses it, and this page explains it rather than just showing "forbidden".
 */

import { Link, useParams } from 'react-router-dom'
import { useEffect, useRef } from 'react'

import { useSession } from '@/app/providers'
import { useAccount, useOrganization } from '@/shared/api/hooks'
import { request } from '@/shared/api/client'
import { useMutation } from '@tanstack/react-query'
import { overviewSchema } from '@/entities/types'
import { Masthead } from '@/shared/ui/Chrome'
import { Button, ErrorNotice, Label, Loading, Notice, Panel } from '@/shared/ui/primitives'

export function AcceptInvitation() {
  const { token = '' } = useParams()
  const { user, ready, signOut } = useSession()
  const { data: account } = useAccount({ enabled: Boolean(user) })
  const attempted = useRef(false)

  const accept = useMutation({
    mutationFn: () =>
      request(`/v1/invitations/${encodeURIComponent(token)}/accept`, {
        method: 'POST',
        schema: overviewSchema,
      }),
  })

  // Accept once, automatically, as soon as there is a session. Making the person
  // click "Accept" after they already clicked an invitation link is a step that
  // exists only to be clicked.
  useEffect(() => {
    if (user && token && !attempted.current) {
      attempted.current = true
      accept.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, token])

  const joined = useOrganization(accept.data?.id)

  if (!ready) {
    return (
      <>
        <Masthead />
        <Loading label="Checking your session" />
      </>
    )
  }

  return (
    <>
      <Masthead />
      <main id="main" className="shell" style={{ paddingBlock: 'var(--s8)', maxWidth: 520 }}>
        <div className="panel panel--flush">
          <div className="panel__head">
            <Label index="00">Team invitation</Label>
          </div>

          <div className="panel__body stack" style={{ ['--gap' as string]: 'var(--s5)' }}>
            {!user && (
              <>
                <h3>You have been invited to an Archis workspace</h3>
                <p className="mono-note">
                  Sign in with the email address the invitation was sent to. An invitation is
                  bound to one mailbox and can only be used once.
                </p>
                <Link
                  to={`/signin?next=/invitations/${encodeURIComponent(token)}`}
                  className="ph-no-capture"
                >
                  <Button variant="primary" block size="lg">
                    Sign in to accept
                  </Button>
                </Link>
              </>
            )}

            {user && accept.isPending && <Loading label="Accepting your invitation" />}

            {user && accept.isError && (
              <>
                <ErrorNotice error={accept.error} />
                <p className="mono-note">
                  You are signed in as <strong>{account?.email ?? user.email}</strong>. If the
                  invitation was sent to a different address, sign out and sign in with that
                  one.
                </p>
                <div className="row">
                  <Button onClick={() => void signOut()}>Sign out</Button>
                  <Link to="/account">
                    <Button variant="ghost">Go to my account</Button>
                  </Link>
                </div>
              </>
            )}

            {accept.isSuccess && (
              <>
                <Notice tone="ok">
                  You have joined <strong>{accept.data.name}</strong>.
                </Notice>

                {joined.data && !joined.data.capabilities['seats.manage'] && (
                  <p className="mono-note">
                    Your role is <strong>{accept.data.role.replace('_', ' ')}</strong>.
                  </p>
                )}

                {/* A member with no seat has joined but cannot use the product; saying
                    so here saves them discovering it at the terminal. */}
                {joined.data?.seats.available === joined.data?.seats.purchased && (
                  <Notice tone="warn">
                    You do not have a seat assigned yet. Ask an administrator to assign one —
                    Archis needs a seat to run under this workspace.
                  </Notice>
                )}

                <Panel title="Next" index="01">
                  <div className="term">
                    <div className="term__bar">terminal</div>
                    <div className="term__body">
                      <div className="term__line">
                        <span className="term__prompt">$</span> chi auth login
                      </div>
                      <div className="term__line">
                        <span className="term__prompt">$</span> chi index .
                      </div>
                    </div>
                  </div>
                </Panel>

                <div className="row">
                  <Link to="/account">
                    <Button variant="primary">Go to my account</Button>
                  </Link>
                  <Link to="/downloads">
                    <Button variant="ghost">Install Archis</Button>
                  </Link>
                </div>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  )
}
